/**
 * NIfTI Manager — Enterprise Volume Loader & Exporter
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [AUDIT] v6.0 — MEMORY SAFE & ASYNC EDITION
 * 
 * ИСПРАВЛЕНИЯ И АРХИТЕКТУРА:
 * 1. [CRITICAL] Устранена утечка памяти: URL.revokeObjectURL теперь вызывается всегда после загрузки тома в RAM.
 * 2. [CRITICAL] Убран хардкодный setTimeout(1000). Внедрено интеллектуальное ожидание завершения загрузки тома.
 * 3. [FIX] Promise-блокировка инициализации: предотвращает гонку (Race Condition) при множественных вызовах init.
 * 4. [FIX] Структурирован экспорт NIfTI-1: убраны "магические числа", добавлена строгая типизация хедера.
 * 5. [PERF] Полная инкапсуляция через ES2022 класс + Телеметрия операций.
 */

import { cornerstone } from '../core/init.js';
import { log } from '../utils/logger.js';
// [9.0.0] NIfTI-том тоже создаётся мимо loadVolume — заносим в реестр,
// иначе после ухода вкладки в фон восстанавливать его будет нечем.
import { rememberVolume } from '../core/volumeRegistry.js';

// [v8 FIX] Fallback: если cornerstone не найден через named export
const _cs = cornerstone || globalThis.cornerstone || null;
function _getCS() {
  if (_cs) return _cs;
  // Попытка динамического импорта уже закешированного модуля
  try { return require('@cornerstonejs/core'); } catch { /* noop */ }
  throw new NiftiModuleError('Cornerstone3D core not available. Ensure init.js exports { cornerstone }.');
}

/**
 * Кастомный класс ошибок для модуля NIfTI
 */
export class NiftiModuleError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = 'NiftiModuleError';
    this.details = details;
  }
}

/**
 * Enterprise NIfTI Manager
 */
class NiftiManager {
  #isRegistered = false;
  #initPromise = null;

  // ==========================================
  // 🛡 TELEMETRY DECORATOR
  // ==========================================
  #withTelemetry(actionName, fn) {
    return async (...args) => {
      const start = performance.now();
      try {
        const result = await fn.apply(this, args);
        const duration = (performance.now() - start).toFixed(2);
        log.debug(`[Metrics] ⏱️ NiftiManager.${actionName} took ${duration}ms`);
        return result;
      } catch (error) {
        log.error(`[Metrics] ❌ NiftiManager.${actionName} FAILED:`, error.message);
        throw new NiftiModuleError(`Failed executing ${actionName}`, { originalError: error });
      }
    };
  }

  // ==========================================
  // 🏗 ИНИЦИАЛИЗАЦИЯ И ЗАГРУЗКА
  // ==========================================

  initLoader = this.#withTelemetry('initLoader', async () => {
    if (this.#isRegistered) return;
    
    // Блокировка от состояния "гонки" (Race Condition), если вызвали init дважды одновременно
    if (this.#initPromise) return this.#initPromise;

    this.#initPromise = (async () => {
      try {
        const niftiLoader = await import('@cornerstonejs/nifti-volume-loader');
        const init = niftiLoader.init || niftiLoader.default?.init;
        if (init) {
          await init();
          log.debug('[NIfTI] Volume loader successfully registered');
        }
        this.#isRegistered = true;
      } catch (e) {
        throw new NiftiModuleError('Loader dependency unavailable', { error: e });
      } finally {
        this.#initPromise = null;
      }
    })();

    return this.#initPromise;
  });

  loadNiftiFile = this.#withTelemetry('loadNiftiFile', async (
    file, 
    viewportIds, 
    renderingEngineId = 'mikhmelRenderingEngine', 
    onProgress = () => {}
  ) => {
    if (!this.isNiftiFile(file)) {
      throw new NiftiModuleError('Invalid file type. Expected .nii or .nii.gz');
    }

    await this.initLoader();
    onProgress(5, 'Чтение NIfTI файла...');

    // Создаём object URL для передачи в Cornerstone
    const objectUrl = URL.createObjectURL(file);
    const volumeId = `nifti:${objectUrl}`;

    try {
      const re = _getCS().getRenderingEngine(renderingEngineId);
      if (!re) throw new NiftiModuleError(`RenderingEngine "${renderingEngineId}" not found`);

      onProgress(20, 'Создание тома...');
      const volume = await _getCS().volumeLoader.createAndCacheVolume(volumeId, {
        imageIds: [volumeId],
      });

      onProgress(40, 'Назначение вьюпортов...');
      const validVpIds = viewportIds.filter(id => re.getViewport(id));
      if (!validVpIds.length) throw new NiftiModuleError('No valid viewports found for rendering');

      try { rememberVolume(volumeId, [volumeId], validVpIds, {}, 'main'); } catch (e) { /* */ }
      await _getCS().setVolumesForViewports(re, [{ volumeId }], validVpIds);

      onProgress(70, 'Загрузка воксельных данных (RAM)...');
      
      // [FIX #2] Интеллектуальное ожидание загрузки тома вместо setTimeout(1000)
      const loadResult = volume.load();
      if (loadResult instanceof Promise) {
        await loadResult; 
      } else {
        // Fallback: ждем события загрузки или проверяем статус
        await new Promise((resolve) => {
          const checkStatus = () => volume.loadStatus?.loaded ? resolve() : setTimeout(checkStatus, 50);
          checkStatus();
        });
      }

      re.renderViewports(validVpIds);
      onProgress(100, '✅ NIfTI успешно загружен');
      log.debug(`[NIfTI] Loaded: ${file.name} → volumeId: ${volumeId}`);

      return volumeId;
    } finally {
      //[FIX #1] ГАРАНТИРОВАННАЯ ОЧИСТКА ПАМЯТИ.
      // Независимо от того, упала ошибка или том загрузился, NIfTI loader уже считал 
      // данные в ArrayBuffer внутри Cornerstone. Ссылку на файл можно уничтожать.
      URL.revokeObjectURL(objectUrl);
      log.debug('[NIfTI] ObjectURL revoked (Memory freed)');
    }
  });

  // ==========================================
  // 🔍 УТИЛИТЫ И ВАЛИДАЦИЯ
  // ==========================================

  isNiftiFile(file) {
    if (!file || !file.name) return false;
    const name = file.name.toLowerCase();
    return name.endsWith('.nii') || name.endsWith('.nii.gz');
  }

  /** [v8] Public check for loader status */
  isLoaderReady() { return this.#isRegistered; }
  _isRegisteredCheck() { return this.#isRegistered; }

  filterNiftiFiles(files) {
    if (!files || !files.length) return[];
    return Array.from(files).filter(f => this.isNiftiFile(f));
  }

  // ==========================================
  // 💾 ЭКСПОРТ ДАННЫХ (БЕЗ ЗАВИСИМОСТЕЙ)
  // ==========================================

  exportToNifti = this.#withTelemetry('exportToNifti', (
    pixels, 
    dims, 
    spacing, 
    filename = `export_${Date.now()}.nii`
  ) => {
    // Валидация входных данных
    if (!pixels || !dims || dims.length !== 3 || !spacing || spacing.length !== 3) {
      throw new NiftiModuleError('Invalid input for NIfTI export. Required: pixels, 3D dims, 3D spacing');
    }

    const [nx, ny, nz] = dims;
    const [sx, sy, sz] = spacing;

    // Константы формата NIfTI-1
    const HEADER_SIZE = 348;
    const EXTENSION_SIZE = 4;
    const DATA_OFFSET = 352;
    const BYTES_PER_VOXEL = 2; // INT16 (Datatype 4)
    
    const dataSize = pixels.length * BYTES_PER_VOXEL;
    const buffer = new ArrayBuffer(HEADER_SIZE + EXTENSION_SIZE + dataSize);
    const view = new DataView(buffer);

    // [FIX #4] Строго типизированная и задокументированная запись хедера
    // true = Little-Endian (стандарт де-факто для NIfTI)

    view.setInt32(0, HEADER_SIZE, true);      // sizeof_hdr

    // Измерения (dim[0..7])
    view.setInt16(40, 3, true);               // dim[0] (ndim = 3)
    view.setInt16(42, nx, true);              // dim[1]
    view.setInt16(44, ny, true);              // dim[2]
    view.setInt16(46, nz, true);              // dim[3]
    view.setInt16(48, 1, true);               // dim[4] (time)
    view.setInt16(50, 1, true);               // dim[5]
    view.setInt16(52, 1, true);               // dim[6]
    view.setInt16(54, 1, true);               // dim[7]

    // Тип данных
    view.setInt16(70, 4, true);               // datatype: 4 = INT16
    view.setInt16(72, 16, true);              // bitpix: 16 бит на воксель

    // Размер вокселей (pixdim[0..7]) в мм
    view.setFloat32(76, 1.0, true);           // pixdim[0] (qfac)
    view.setFloat32(80, sx, true);            // pixdim[1]
    view.setFloat32(84, sy, true);            // pixdim[2]
    view.setFloat32(88, sz, true);            // pixdim[3]

    view.setFloat32(108, DATA_OFFSET, true);  // vox_offset (смещение до данных)
    
    // Масштабирование (интенсивность = value * scl_slope + scl_inter)
    view.setFloat32(112, 1.0, true);          // scl_slope
    view.setFloat32(116, 0.0, true);          // scl_inter

    view.setUint8(123, 0x02);                 // xyzt_units: 2 = миллиметры (NIFTI_UNITS_MM)

    // Magic string: "n+1\0" (NIfTI-1 single file)
    view.setUint8(344, 0x6E); // n
    view.setUint8(345, 0x2B); // +
    view.setUint8(346, 0x31); // 1
    view.setUint8(347, 0x00); // \0

    // Extension: 4 нулевых байта
    view.setInt32(HEADER_SIZE, 0, true);

    // --- Запись воксельных данных ---
    const dataArray = new Int16Array(buffer, DATA_OFFSET);
    if (pixels instanceof Int16Array) {
      // Если это уже Int16Array, копирование происходит на скорости C++
      dataArray.set(pixels);
    } else {
      // Иначе конвертируем с округлением (O(N) операция)
      for (let i = 0; i < pixels.length; i++) {
        dataArray[i] = Math.round(pixels[i]);
      }
    }

    // --- Trigger Download ---
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    
    a.href = url;
    a.download = filename;
    document.body.appendChild(a); // Безопасный клик (некоторые браузеры требуют присутствия в DOM)
    a.click();
    document.body.removeChild(a);
    
    // Очистка памяти после скачивания
    setTimeout(() => URL.revokeObjectURL(url), 10000);

    const mbSize = (buffer.byteLength / 1024 / 1024).toFixed(1);
    log.debug(`[NIfTI] Export successfully triggered: ${filename} (${mbSize} MB)`);
    return blob;
  });
}

// ==========================================
// 🛡 100% BACKWARDS COMPATIBLE API EXPORTS
// ==========================================
// Сохраняем все твои импорты в других файлах нетронутыми.

const _niftiManager = new NiftiManager();

export function initNiftiLoader() { return _niftiManager.initLoader(); }
export function loadNiftiFile(file, viewportIds, renderingEngineId, onProgress) { 
  return _niftiManager.loadNiftiFile(file, viewportIds, renderingEngineId, onProgress); 
}
export function isNiftiFile(file) { return _niftiManager.isNiftiFile(file); }
export function filterNiftiFiles(files) { return _niftiManager.filterNiftiFiles(files); }
export function exportToNifti(pixels, dims, spacing, filename) { 
  return _niftiManager.exportToNifti(pixels, dims, spacing, filename); 
}

// ── [v8] Enterprise API ──
export function isNiftiLoaderReady() { return _niftiManager.isLoaderReady(); }
export function getNiftiModuleState() {
  return {
    loaderRegistered: _niftiManager._isRegisteredCheck(),
    cornerstoneAvailable: !!_cs,
    supportedExtensions: ['.nii', '.nii.gz'],
  };
}